import { createContext, useContext, useMemo } from "react";
import { Box, Grid, IconButton, Tooltip, Typography, lighten, useMediaQuery, useTheme } from "@mui/material";
import { Delete, Download } from "@mui/icons-material";
import { DataGridPro, GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import {
	FileUploader,
	MimeTypes,
	PageSection,
	useContainerTransportOrderContext,
	usePopupAlert
} from "@sal/onevent-portal";
import { HerculesDocument, useHerculesDocuments } from "../hooks";
import { MOVEContainerTransportGeneralFreightOrder } from "../models";

export type SubmittedEditDocumentState = {
	stagedUploads: File[];
	stagedDeletions: string[];
	addStagedUploads: (files: File[]) => void;
	removeStagedUpload: (file: File) => void;
	addStagedDeletion: (fileName: string) => void;
	removeStagedDeletion: (fileName: string) => void;
};

export const SubmittedEditDocumentContext = createContext<SubmittedEditDocumentState | null>(null);

type DocumentRow = {
	rowId: string;
	fileName: string;
	name: string;
	displayType: "Existing" | "Staged Upload";
	isPendingDeletion: boolean;
	isLocalUpload: boolean;
	file?: File;
	herculesDoc?: HerculesDocument;
};

const apiPathname = process.env.ONEVENT_API_PATHNAME || "";

function herculesUrl(action: string, consignmentNumber: string, extra?: string): string {
	const base = `${apiPathname}/hercules/${action}/${encodeURIComponent(consignmentNumber)}`;
	return extra ? `${base}/${encodeURIComponent(extra)}` : base;
}

function triggerBrowserDownload(blob: Blob, name: string) {
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement("a");
	anchor.href = url;
	anchor.download = name;
	document.body.appendChild(anchor);
	anchor.click();
	document.body.removeChild(anchor);
	URL.revokeObjectURL(url);
}

export function SubmittedEditDocuments() {
	const { query } = useContainerTransportOrderContext();
	const consignmentNumber =
		(query.data?.data as MOVEContainerTransportGeneralFreightOrder | undefined)?.reference ?? undefined;
	const docState = useContext(SubmittedEditDocumentContext);
	const popup = usePopupAlert();
	const theme = useTheme();
	const mobileBreakPoint = useMediaQuery(theme.breakpoints.down("md"));

	const { files: herculesFiles, isLoading } = useHerculesDocuments(consignmentNumber);

	const existingDocuments = useMemo(() => herculesFiles.filter((f) => f.type === "Document"), [herculesFiles]);

	const stagedUploads = docState?.stagedUploads ?? [];
	const stagedDeletions = docState?.stagedDeletions ?? [];

	const displayRows = useMemo<DocumentRow[]>(() => {
		const existingRows: DocumentRow[] = existingDocuments.map((doc) => ({
			rowId: `existing-${doc.fileName}`,
			fileName: doc.fileName,
			name: doc.name,
			displayType: "Existing",
			isPendingDeletion: stagedDeletions.includes(doc.fileName),
			isLocalUpload: false,
			herculesDoc: doc
		}));

		const uploadRows: DocumentRow[] = stagedUploads.map((file) => ({
			rowId: `upload-${file.name}-${file.size}-${file.lastModified}`,
			fileName: file.name,
			name: file.name,
			displayType: "Staged Upload",
			isPendingDeletion: false,
			isLocalUpload: true,
			file
		}));

		return [...existingRows, ...uploadRows];
	}, [existingDocuments, stagedUploads, stagedDeletions]);

	const handleAddFiles = (newFiles: File[]) => {
		docState?.addStagedUploads(newFiles);
	};

	const handleToggleDeletion = (row: DocumentRow) => {
		if (row.isLocalUpload && row.file) {
			docState?.removeStagedUpload(row.file);
			return;
		}

		if (stagedDeletions.includes(row.fileName)) {
			docState?.removeStagedDeletion(row.fileName);
		} else {
			docState?.addStagedDeletion(row.fileName);
		}
	};

	const handleDownload = async (row: DocumentRow) => {
		if (!consignmentNumber || row.isLocalUpload) {
			return;
		}

		try {
			popup({ severity: "info", message: "Downloading..." });
			const response = await fetch(herculesUrl("GetDocument", consignmentNumber, row.fileName));

			if (!response.ok) {
				throw new Error("Download failed.");
			}

			const blob = await response.blob();
			triggerBrowserDownload(blob, row.fileName);
		} catch (err) {
			popup({
				severity: "error",
				message: err instanceof Error ? err.message : "Download failed."
			});
		}
	};

	const columns: GridColDef<DocumentRow>[] = [
		{
			field: "fileName",
			headerName: "File Name",
			flex: 1,
			minWidth: 200,
			display: "flex",
			renderCell: (params: GridRenderCellParams<DocumentRow>) => (
				<Typography
					variant="body2"
					sx={{
						textDecoration: params.row.isPendingDeletion ? "line-through" : "none",
						color: params.row.isPendingDeletion ? "text.disabled" : "text.primary"
					}}
				>
					{params.row.fileName}
				</Typography>
			)
		},
		{
			field: "reference",
			headerName: "Reference",
			flex: 1,
			display: "flex",
			renderCell: (params: GridRenderCellParams<DocumentRow>) => (
				<Typography
					variant="body2"
					sx={{
						textDecoration: params.row.isPendingDeletion ? "line-through" : "none",
						color: params.row.isPendingDeletion ? "text.disabled" : "text.secondary"
					}}
				>
					{params.row.name}
				</Typography>
			)
		},
		{
			field: "actions",
			headerName: "",
			width: 110,
			display: "flex",
			sortable: false,
			filterable: false,
			disableColumnMenu: true,
			renderCell: (params: GridRenderCellParams<DocumentRow>) => (
				<Box display="flex" alignItems="center" gap={1}>
					{!params.row.isLocalUpload && (
						<Tooltip title="Download">
							<span>
								<IconButton
									size="small"
									color="primary"
									disabled={params.row.isPendingDeletion}
									onClick={async () => {
										await handleDownload(params.row);
									}}
								>
									<Download fontSize="small" />
								</IconButton>
							</span>
						</Tooltip>
					)}
					<Tooltip
						title={
							params.row.isPendingDeletion
								? "Undo removal"
								: params.row.isLocalUpload
									? "Remove"
									: "Mark for removal"
						}
					>
						<IconButton
							size="small"
							color={params.row.isPendingDeletion ? "warning" : "error"}
							onClick={() => handleToggleDeletion(params.row)}
						>
							<Delete fontSize="small" />
						</IconButton>
					</Tooltip>
				</Box>
			)
		}
	];

	const fileUploaderProps = {
		onFilesChange: handleAddFiles,
		acceptedFileTypes: [
			MimeTypes.pdf,
			MimeTypes.jpeg,
			MimeTypes.doc,
			MimeTypes.docx,
			MimeTypes.png,
			MimeTypes.csv,
			MimeTypes.xls,
			MimeTypes.xlsx,
			MimeTypes.msg
		],
		multiple: true,
		boxStyle: {
			backgroundColor: lighten(theme.palette.primary.main, 0.9),
			border: `2px dashed ${lighten(theme.palette.primary.main, 0.7)}`,
			borderRadius: "5px",
			padding: "20px",
			boxSizing: "border-box" as const,
			height: "100%"
		},
		customDropzoneContent: (
			<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
				<i className="fa fa-file-upload" style={{ fontSize: "40px", color: theme.palette.primary.main }} />
				<Typography
					variant="body1"
					sx={{ color: theme.palette.common.black, fontWeight: "500", fontSize: "1rem" }}
				>
					Drag & drop files or{" "}
					<span style={{ color: theme.palette.primary.main, fontWeight: "bold", cursor: "pointer" }}>
						Browse
					</span>
				</Typography>
				<Typography variant="caption" sx={{ color: "#bdbdbd", fontSize: "0.75rem" }} />
			</Box>
		),
		dropzoneStyles: {
			backgroundColor: lighten(theme.palette.primary.main, 0.9),
			border: "none"
		}
	};

	const grid = (
		<DataGridPro<DocumentRow>
			rows={displayRows}
			columns={columns}
			getRowId={(row) => row.rowId}
			rowSelection={false}
			hideFooter
			autoHeight
			loading={isLoading}
			localeText={{ noRowsLabel: "No documents" }}
			sx={{
				"& .MuiDataGrid-columnHeaders": {
					position: "relative",
					zIndex: 1,
					"&::before": {
						content: '""',
						position: "absolute",
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						backgroundColor: theme.palette.common.white,
						zIndex: -1
					}
				},
				"& .MuiDataGrid-columnHeader, & .MuiDataGrid-scrollbarFiller": {
					backgroundColor: `${theme.palette.common.white} !important`
				},
				"& .MuiDataGrid-columnHeader": {
					color: theme.palette.common.black,
					fontWeight: 600
				},
				"& .MuiDataGrid-columnHeaderTitle": {
					color: theme.palette.common.black,
					fontWeight: 600,
					fontSize: "0.9rem"
				},
				"& .MuiDataGrid-cell": {
					py: "12px",
					px: "8px",
					alignItems: "center"
				},
				"& .MuiInputBase-root": {
					fontSize: "0.85rem"
				},
				"& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader": {
					borderBottom: "1px solid #e0e0e0"
				},
				"& .MuiDataGrid-footerContainer": {
					display: "none"
				}
			}}
		/>
	);

	return (
		<PageSection title="Documents">
			{mobileBreakPoint ? (
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<FileUploader {...fileUploaderProps} />
					</Grid>
					<Grid item xs={12}>
						{grid}
					</Grid>
				</Grid>
			) : (
				<Grid container spacing={2} alignItems="stretch">
					<Grid item xs={6} sx={{ display: "flex" }}>
						<Box width="100%" height="100%" display="flex">
							<FileUploader {...fileUploaderProps} />
						</Box>
					</Grid>
					<Grid item xs={6}>
						{grid}
					</Grid>
				</Grid>
			)}
		</PageSection>
	);
}
